<template>
  <div class="text-center">
    <img src="../assets/icons/cv-tool-gold.png" alt="cv-tool.com Logo" class="logo-image" />
    <button @click="$emit('close')" class="modal-close-btn">
      <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
      </svg>
    </button>
    <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 dark:bg-green-900 mb-4">
      <svg class="h-6 w-6 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
      </svg>
    </div>
    <h2 class="signup-title">Account Created Successfully!</h2>
    <p class="signup-subtitle mb-6">
      We've sent a confirmation email to <strong>{{ email }}</strong>. 
      Please check your inbox and click the confirmation link to activate your account.
    </p>
    
    <!-- Error message for resend attempts -->
    <div v-if="error" class="bg-red-50 dark:bg-red-900 p-4 rounded-md mb-4">
      <div class="flex">
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
          </svg>
        </div>
        <div class="ml-3">
          <h3 class="text-sm font-medium text-red-800 dark:text-red-200">
            {{ error }}
          </h3>
        </div>
      </div>
    </div>

    <!-- Success message for resend attempts -->
    <div v-if="resendSuccess" class="bg-green-50 dark:bg-green-900 p-4 rounded-md mb-4">
      <div class="flex">
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
          </svg>
        </div>
        <div class="ml-3">
          <h3 class="text-sm font-medium text-green-800 dark:text-green-200">
            Confirmation email sent! Please check your inbox.
          </h3>
        </div>
      </div>
    </div>
    
    <div class="space-y-3">
      <button
        @click="goToLogin"
        class="signup-btn"
      >
        Go to Sign In
      </button>
      <button
        @click="handleResendConfirmation"
        :disabled="isResending"
        class="w-full flex justify-center py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
      >
        <svg 
          v-if="isResending" 
          class="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-500" 
          xmlns="http://www.w3.org/2000/svg" 
          fill="none" 
          viewBox="0 0 24 24"
        >
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        {{ isResending ? 'Sending...' : 'Resend Confirmation Email' }}
      </button>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue'

export default {
  name: 'EmailConfirmationSuccess',
  props: {
    email: {
      type: String,
      required: true
    }
  },
  emits: ['close', 'go-to-login'],
  setup(props, { emit }) {
    const isResending = ref(false)
    const error = ref('')
    const resendSuccess = ref(false)

    const getCSRFToken = () => {
      const meta = document.querySelector('meta[name="csrf-token"]')
      return meta ? meta.getAttribute('content') : null
    }

    const handleResendConfirmation = async () => {
      try {
        isResending.value = true
        error.value = ''
        resendSuccess.value = false

        const response = await fetch('/users/confirmation', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': getCSRFToken(),
          },
          body: JSON.stringify({
            user: {
              email: props.email
            }
          })
        })

        const data = await response.json()

        if (data.success) {
          resendSuccess.value = true
          error.value = ''
          
          // Clear success message after a few seconds
          setTimeout(() => {
            resendSuccess.value = false
          }, 5000)
        } else {
          error.value = data.message || 'Failed to resend confirmation email'
        }
      } catch (err) {
        error.value = 'An error occurred while resending confirmation email. Please try again.'
        console.error('Resend confirmation error:', err)
      } finally {
        isResending.value = false
      }
    }

    const goToLogin = () => {
      emit('go-to-login')
    }

    return {
      isResending,
      error,
      resendSuccess,
      handleResendConfirmation,
      goToLogin
    }
  }
}
</script>

<style scoped>
.logo-image {
  width: auto;
  height: 40px;
  margin: 0 auto 1.5rem;
  display: block;
}

.modal-close-btn {
  position: absolute;
  top: 1rem;
  right: 1rem;
  color: #6b7280;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 0.375rem;
  transition: all 0.2s ease;
}

.modal-close-btn:hover {
  color: #374151;
  background-color: #f3f4f6;
}

.dark .modal-close-btn {
  color: #9ca3af;
}

.dark .modal-close-btn:hover {
  color: #d1d5db;
  background-color: #374151;
}

.signup-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.5rem;
}

.dark .signup-title {
  color: #f9fafb;
}

.signup-subtitle {
  color: #6b7280;
  line-height: 1.5;
}

.dark .signup-subtitle {
  color: #9ca3af;
}

.signup-btn {
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 0.75rem 1rem;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  color: white;
  background: #000537;
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.signup-btn:hover {
  opacity: 0.9;
}

.signup-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>